import { useCallback, useMemo, useState } from 'react';
import {
  Divider,
  Dropdown,
  IconThreeDotsVer,
  Token,
  Tooltip,
  Typography,
  useDropdownState,
  Wrapper
} from '@screentone/core';
import classnames from 'classnames';

import { HelpIcon } from 'components';
import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
import { QueryRulesForm } from 'components/query-rules-form/QueryRulesForm';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { ListContainer } from 'data/generated/graphql';
import { getAllessehJsonQuery, setJsonQueryStr } from 'utils/collection';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { ManualItems } from './components/ManualItems/ManualItems';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { QueryRulesItems } from './components/QueryItems/QueryRulesItems';
import styles from './List.module.scss';
import { formatNumItemsText, getTokenState, WARNING_THRESHOLD } from './List.utils';
import collectionEditStyles from '../../../../features/collection-edit/CollectionEdit.module.scss';

import { EntityType } from 'contexts/datamodel/DataModelContext.constants';

interface ListProps extends CommonEntityProps {
  data: ListContainer;
}

export const List = ({ data: list, parentHierarchyId, index, isReadOnly = false, isHistory = false }: ListProps) => {
  const { generateHierarchyId, modifyEntity } = useDataModelContext();
  const { renderActions: renderMenuActions, hasRenderActions: hasRenderMenuActions } = useContextMenuActions();

  const { open, setOpen, componentRef } = useDropdownState();
  const handleDropdownClick = useCallback(() => {
    setOpen(!open);
  }, [setOpen, open]);

  const hierarchyId = generateHierarchyId(list, parentHierarchyId, index);

  const articleItems = list.collection.filter(
    (item) => item.type === EntityType.Article || item.type === EntityType.LinkedItem
  );
  const lastArticleItemIndex = list.collection.findLastIndex((item) => item.type === EntityType.Article);

  const queryItemIndex = list.collection.findIndex((item) => item.type === EntityType.Query);
  const allessehJsonQuery = getAllessehJsonQuery(list);

  const [queryItemsCount, setQueryItemsCount] = useState<number>(0);

  const handleNumItemsLoaded = useCallback((numItemsFromQuery: number) => {
    setQueryItemsCount((prev) => (prev === numItemsFromQuery ? prev : numItemsFromQuery));
  }, []);

  const totalItemsCount =
    articleItems.filter((item) => !('status' in item.attributes && item.attributes.status === 'embargo')).length +
    queryItemsCount;

  const tokenState = getTokenState(totalItemsCount);
  const tokenIcon = tokenState.icon;
  const tokenColor = tokenState.color;
  const tokenTooltipText = tokenState.tooltipText;
  const numItemsText = formatNumItemsText(totalItemsCount);
  const params = useMemo(
    () => ({
      entity: list,
      hierarchyId,
      index,
      extraProperties: {
        setOpen: handleDropdownClick
      }
    }),
    [list, hierarchyId, index, handleDropdownClick]
  );

  return (
    <>
      {!isHistory && (
        <Wrapper
          data-testid="collection-items-section-container"
          className={styles.collectionItemsHeader}
          margin={{ bottom: 'md' }}
          data-hierarchy-id={hierarchyId}
        >
          <div data-testid="collection-items-section-header" className={styles.collectionItemsHeaderLeft}>
            <Typography
              data-testid="collection-items-section-header-title"
              variant="header3"
              margin={{ bottom: 'none', right: 'sm' }}
            >
              Collection Items
            </Typography>
            <Tooltip
              className={classnames(
                styles.numItemsTooltip,
                totalItemsCount >= WARNING_THRESHOLD ? '' : styles.numItemsTooltipHidden
              )}
            >
              {totalItemsCount >= WARNING_THRESHOLD && (
                <Tooltip.Content position="top">{tokenTooltipText}</Tooltip.Content>
              )}
              <Tooltip.Trigger>
                <Token color={tokenColor} icon={tokenIcon} data-testid="collection-items-section-num-items">
                  {numItemsText}
                </Token>
              </Tooltip.Trigger>
            </Tooltip>
          </div>
          <HelpIcon
            data-testid="collection-items-section-help-icon"
            text="A collection is a group of curated content and/or an auto-generated list of content from a query. Collections are used for sharing the same content across multiple pages."
          />
        </Wrapper>
      )}
      <div data-testid="collection-items-section-content-container" data-model-hierarchy-id={hierarchyId}>
        <Wrapper
          data-testid="collection-items-section-content"
          padding={{ top: 'md', horizontal: 'md' }}
          className={classnames(styles.collectionItemsWrapper, collectionEditStyles.itemsScrollable)}
        >
          <div data-testid="collection-items-section-background" className={styles.collectionItemsWrapperBackground} />
          <Wrapper
            data-testid="collection-items-section-wrapper"
            className={styles.collectionItemsSectionWrapper}
            margin={{ bottom: 'sm' }}
          >
            <Typography data-testid="collection-items-section-content-title" weight="bold" size="sm">
              Curated List
            </Typography>
            {hasRenderMenuActions(MenuActionsKeys.CollectionList, params) && (
              <Dropdown
                open={open}
                padding={{ all: 'none' }}
                position="right"
                trigger={<IconThreeDotsVer color="asphalt" />}
                data-testid="page-draft-ellipsis-icon"
                onToggle={handleDropdownClick}
                componentRef={componentRef}
              >
                {renderMenuActions(MenuActionsKeys.CollectionList, params)}
              </Dropdown>
            )}
          </Wrapper>
          <ManualItems
            articles={articleItems}
            allItems={list.collection}
            allessehJsonQuery={allessehJsonQuery}
            setJsonQueryStr={(newQueryStr) => setJsonQueryStr(newQueryStr, queryItemIndex, modifyEntity)}
            listHierarchyId={hierarchyId}
            isReadOnly={isReadOnly}
            isHistory={isHistory}
          />
          {(!isHistory || allessehJsonQuery) && (
            <>
              <Divider data-testid="collection-items-section-divider" margin={{ top: 'md', bottom: 'md' }} />
              <Typography
                data-testid="collection-items-section-query-title"
                weight="bold"
                size="sm"
                margin={{ bottom: 'sm' }}
              >
                Queried List
              </Typography>
              {isHistory ? (
                <QueryRulesForm
                  allessehJsonQuery={allessehJsonQuery}
                  onAllessehJsonQueryChange={null}
                  onQueryRuleChange={null}
                />
              ) : (
                <QueryRulesItems
                  allessehJsonQuery={allessehJsonQuery}
                  lastArticleItemIndex={lastArticleItemIndex}
                  setJsonQueryStr={(newQueryStr) => setJsonQueryStr(newQueryStr, queryItemIndex, modifyEntity)}
                  onNumItemsLoaded={handleNumItemsLoaded}
                />
              )}
            </>
          )}
        </Wrapper>
      </div>
    </>
  );
};
